import { Link as BalderLink, Paragraph } from "@digdir/designsystemet-react";
import { ChevronLeftIcon } from "@navikt/aksel-icons";
import { Link, useRouter } from "@tanstack/react-router";
import type { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";
import type { FileRoutesByTo } from "@/routeTree.gen";
import { cn } from "@/utils";

export function Page({
  children,
  className,
  withBackLink = false,
  backLinkTo,
  withoutPadding = false,
  actionBar = null,
}: PropsWithChildren<{
  className?: string;
  withBackLink?: boolean;
  backLinkTo?: keyof FileRoutesByTo | "history";
  withoutPadding?: boolean;
  actionBar?: React.ReactNode[] | null;
}>) {
  return (
    <article
      className={cn(
        "w-full space-y-4 my-12 pb-12 px-5 max-w-page sm:px-15",
        "bg-balder-background-default",
        withBackLink ? "" : "",
        withoutPadding ? "" : "mx-auto",
        className,
      )}
    >
      {(withBackLink || actionBar) && (
        <section className="pb-6 flex items-center justify-between flex-wrap gap-6">
          {withBackLink && <BackLink to={backLinkTo} />}
          {actionBar && <ActionBar actions={actionBar} />}
        </section>
      )}
      {children}
    </article>
  );
}

function BackLink({ to }: { to?: keyof FileRoutesByTo | "history" }) {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <Paragraph data-size="md" asChild>
      {to === "history" ? (
        <BalderLink
          href="#"
          onClick={() => router.history.back()}
          className="text-inherit flex items-center gap-2"
        >
          <ChevronLeftIcon fontSize="2rem" />
          <span>{t("button.back")}</span>
        </BalderLink>
      ) : to != null ? (
        <BalderLink asChild>
          <Link to={to} className="text-inherit flex items-center gap-2">
            <ChevronLeftIcon fontSize="2rem" />
            <span>{t("button.back")}</span>
          </Link>
        </BalderLink>
      ) : (
        <BalderLink asChild>
          <Link to={"/"} className="text-inherit flex items-center gap-2">
            <ChevronLeftIcon fontSize="2rem" />
            <span>{t("button.back-to-frontpage")}</span>
          </Link>
        </BalderLink>
      )}
    </Paragraph>
  );
}

function ActionBar({ actions }: { actions: React.ReactNode[] }) {
  return <div className="flex gap-4">{actions}</div>;
}
